/**
 * Map number x from range [a, b] to [c, d]
 */
export const map = (x: number, a: number, b: number, c: number, d: number): number => ((x - a) * (d - c)) / (b - a) + c;

/**
 * Preload images using modern browser APIs
 */
export const preloadImages = (selector = 'img'): Promise<void> => {
  return new Promise(resolve => {
    const images = document.querySelectorAll<HTMLImageElement>(selector);
    const promises = Array.from(images).map(img => {
      if (img.complete) {
        return Promise.resolve();
      }
      return new Promise<void>((imgResolve, imgReject) => {
        img.onload = () => imgResolve();
        img.onerror = () => imgReject();
      });
    });

    Promise.allSettled(promises).then(() => resolve());
  });
};

/**
 * Gets the center position of an element
 */
export const getElementCenter = (element: HTMLElement): { x: number; y: number } => ({
  x: element.offsetLeft + element.offsetWidth / 2,
  y: element.offsetTop + element.offsetHeight / 2,
});

/**
 * Gets the distance between two elements (element's center)
 */
export const getDistance = (element1: HTMLElement, element2: HTMLElement): number => {
  const center1 = getElementCenter(element1);
  const center2 = getElementCenter(element2);
  return Math.hypot(center1.x - center2.x, center1.y - center2.y);
};

/**
 * Calculates how much (x and y) the element1 needs to move away from element2 for a distance of [spread]px
 */
export const getTranslationDistance = (
  element1: HTMLElement,
  element2: HTMLElement,
  spread = 80,
  maxDistance = 500
): { x: number; y: number } => {
  const center1 = getElementCenter(element1);
  const center2 = getElementCenter(element2);

  const adjustedSpread = Math.max(map(getDistance(element1, element2), 0, maxDistance, spread, 0), 0);

  const angle = Math.atan2(Math.abs(center2.y - center1.y), Math.abs(center2.x - center1.x));

  const x = Math.abs(Math.cos(angle) * adjustedSpread);
  const y = Math.abs(Math.sin(angle) * adjustedSpread);

  return {
    x: center1.x < center2.x ? x * -1 : x,
    y: center1.y < center2.y ? y * -1 : y,
  };
};
